import React, {Fragment, useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Stack,
    Text,
    Heading,
    useBreakpointValue,
    Image,
    Link,
    Button,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import Suggestions from './suggestions'
import RecommendedProducts from '../../recommended-products'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {KS_SEARCH_URL, YR_SEARCH_URL} from '../../../constants'
import useTagManager from '../../../hooks/use-tag-manager'
import {searchUrlBuilder} from '../../../utils/url'
import {useSiteContext} from '../../../contexts/site'
import {SwiperSlide, Swiper} from 'swiper/react'
import {Navigation} from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import {useProducts} from '@salesforce/commerce-sdk-react'

const EinsteinSearchSuggestions = ({closeAndNavigate, searchQuery, recentSearches}) => {
    const {isYR} = useSiteContext()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {yrNoSearchSuggestionsRecommenderId, ksNoSearchSuggestionsRecommenderId} =
        commerceAPI.storefront
    return (isYR ? yrNoSearchSuggestionsRecommenderId : ksNoSearchSuggestionsRecommenderId) ? (
        <RecommendedProducts // don't render the recommender if the site preference with ID: yrNoSearchSuggestionsRecommenderId/ noSearchSuggestionsRecommenderId is not set.
            title={
                <FormattedMessage
                    defaultMessage="Product Suggestions"
                    id="search.einstein.product.suggestions"
                />
            }
            isEmptySearch={true}
            searchQuery={searchQuery}
            recentSearches={recentSearches}
            closeAndNavigate={closeAndNavigate}
            recommender={
                isYR ? yrNoSearchSuggestionsRecommenderId : ksNoSearchSuggestionsRecommenderId
            }
        />
    ) : (
        <></>
    )
}

EinsteinSearchSuggestions.propTypes = {
    closeAndNavigate: PropTypes.func,
    recentSearches: PropTypes.any,
    searchQuery: PropTypes.string
}

export const SearchSugggestionTile = ({
    product,
    closeAndNavigate,
    searchQuery,
    totalSuggestionsLength
}) => {
    const suggestionsStyle = useStyleConfig('SuggestionsStyle')
    const tagManager = useTagManager()
    const {
        name,
        price,
        productId,
        link,
        slugUrl,
        currency,
        c_globalePrice,
        c_selectedCurrency,
        c_selectedCurrencySymbol
    } = product
    const imageUrl = product.disBaseLink || product?.c_images?.smallImage || '/noimage.png'
    const intl = useIntl()
    const priceText = intl.formatNumber(c_globalePrice || price, {
        style: 'currency',
        currency: c_selectedCurrency || currency
    })

    const url = slugUrl || link
    const ksRedirection = url?.indexOf('/yellow-rose/') === -1
    return (
        <Link
            onClick={(e) => {
                e.preventDefault()
                tagManager.siteSearch(
                    searchQuery,
                    totalSuggestionsLength?.toString() || '0',
                    undefined,
                    'product-rec',
                    searchQuery
                )
                e.preventDefault()
                closeAndNavigate(url, false, !!ksRedirection)
            }}
            display="block"
            href={url}
            _hover={{textDecoration: 'none'}}
            key={productId}
        >
            <Box display={{lg: 'contents', base: 'contents'}} overflow="hidden">
                {/** no need to serve dynamic images here **/}
                <Box
                    className="image-wrapper"
                    background="gray.100"
                    borderRadius="0"
                    width="fit-content"
                >
                    <Image
                        src={imageUrl}
                        alt={name}
                        width="152px"
                        height="190px"
                        objectFit="cover"
                        borderRadius="0"
                        mixBlendMode="multiply"
                    />
                </Box>
                <Box {...suggestionsStyle.productInfoBox.main}>
                    <Heading
                        {...suggestionsStyle.productInfoBox.productName}
                        dangerouslySetInnerHTML={{__html: name}}
                    />
                    <Text {...suggestionsStyle.productInfoBox.productPrice}>
                        {priceText.replace(
                            c_selectedCurrency,
                            c_selectedCurrencySymbol || currency
                        )}
                    </Text>
                </Box>
            </Box>
        </Link>
    )
}

SearchSugggestionTile.propTypes = {
    product: PropTypes.object,
    searchQuery: PropTypes.string,
    totalSuggestionsLength: PropTypes.any,
    closeAndNavigate: PropTypes.func
}

const RecentSearches = ({
    recentSearches,
    closeAndNavigate,
    totalSuggestionsLength,
    searchQuery
}) => {
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const suggestionsStyle = useStyleConfig('SuggestionsStyle')
    const tagManager = useTagManager()
    const {isYR} = useSiteContext()
    const recentSearchProps = {
        sx: {
            display: '-webkit-box',
            '-webkit-line-clamp': '1',
            '-webkit-box-orient': 'vertical',
            width: '200px',
            overflow: 'hidden',
            'white-space': 'pre-wrap',
            'text-align': 'left'
        }
    }
    const handleAction = (term) => {
        tagManager.siteSearch(
            searchQuery,
            totalSuggestionsLength?.toString() || '0',
            undefined,
            'recent-searches',
            term
        )
        closeAndNavigate((isYR ? YR_SEARCH_URL : KS_SEARCH_URL) + term, false, false)
    }

    return (
        <Stack spacing={0} data-testid="sf-recent-searches">
            <Box display="contents">
                {recentSearches?.map((term, idx) => (
                    <Button
                        aria-label={term}
                        onMouseDown={() => {
                            handleAction(term)
                        }}
                        onKeyDown={(e) => {
                            const keys = new Set(['Enter', 'Space', 'NumpadEnter'])

                            if (e.code === 'Space') {
                                e.preventDefault()
                            }

                            if (keys.has(e.code)) {
                                handleAction(term)
                            }
                        }}
                        key={idx}
                        {...suggestionsStyle.term.button}
                        _hover={{
                            backgroundColor: isDesktop ? 'gray.100' : 'transparent',
                            borderRadius: '10px',
                            color: isDesktop ? 'teal.400' : 'black'
                        }}
                        padding="0px"
                        minWidth={{base: 'fit-content', lg: '380px'}}
                        width={{base: 'fit-content', lg: '100%'}}
                    >
                        <Text {...suggestionsStyle.term.text} {...recentSearchProps}>
                            {term}
                        </Text>
                    </Button>
                ))}
            </Box>
        </Stack>
    )
}

RecentSearches.propTypes = {
    recentSearches: PropTypes.array,
    closeAndNavigate: PropTypes.func,
    enableBloomreachSuggestions: PropTypes.bool,
    totalSuggestionsLength: PropTypes.any,
    searchQuery: PropTypes.string
}

const SearchSuggestions = ({
    searchQuery,
    phraseResponses = {},
    categoryResponses = {},
    searchSuggestions,
    closeAndNavigate,
    recentSearches,
    enableBloomreachSuggestions
}) => {
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const suggestionsStyle = useStyleConfig('SuggestionsStyle')
    const tagManager = useTagManager()
    const {isYR} = useSiteContext()
    const [productSuggestionsToRender, setProductSuggestionsToRender] = useState(
        searchSuggestions?.productSuggestions
    )

    useEffect(() => {
        if (searchSuggestions?.productSuggestions) {
            setProductSuggestionsToRender(searchSuggestions.productSuggestions)
        }
    }, [searchSuggestions?.productSuggestions])
    const [recommendedForText, setRecommendedForText] = useState(searchQuery)
    const hasTrackedRef = useRef(false)
    const productIds = searchSuggestions?.productSuggestions?.map((p) => p.productId) || []

    const {data: productsData} = useProducts({
        parameters: {
            ids: productIds
        },
        enabled: productIds.length > 0
    })

    useEffect(() => {
        if (!productsData?.data?.length) return
        if (hasTrackedRef.current) return

        hasTrackedRef.current = true

        window.__dataLayerLoaded = true

        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
        const viewListBatchSize = isMobile ? 4 : 6
        try {
            const apiHits = productsData.data?.map((hit, i) => ({...hit, index: i}))
            for (let i = 0; i < apiHits?.length; i += viewListBatchSize) {
                const batch = apiHits.slice(i, i + viewListBatchSize)
                tagManager.viewItemLists(batch, undefined, {
                    method: 'search',
                    listId: 'search suggestions',
                    listName: 'search suggestions'
                })
            }
        } catch (e) {
            console.log(e.message)
        }

        window.__dataLayerLoaded = false
    }, [productsData, searchQuery])

    useEffect(() => {
        hasTrackedRef.current = false
    }, [searchQuery])

    return (
        <Stack {...suggestionsStyle.container} margin="auto">
            <Fragment>
                <Box
                    className="phrase-categories-mobile-wrapper"
                    {...suggestionsStyle.phaseCategoriesWrapper.main}
                    gap="20px"
                    display={
                        searchSuggestions?.phraseSuggestion?.length ||
                        searchSuggestions?.categorySuggestions?.length ||
                        recentSearches?.length
                            ? 'grid'
                            : 'none'
                    }
                    padding={{
                        lg: '3rem 1rem 1rem 2.5rem'
                    }}
                    height={{
                        lg: 'max-content'
                    }}
                >
                    {enableBloomreachSuggestions &&
                    searchSuggestions?.phraseSuggestion?.length &&
                    searchQuery ? (
                        <Box className="search-phase-wrapper">
                            <Text
                                fontSize={{base: '16px', lg: '14px'}}
                                className="suggested-terms"
                                display="contents"
                                color={{base: 'gray.800', lg: 'yrBlack'}}
                            >
                                <Heading
                                    {...suggestionsStyle.phaseCategoriesWrapper.heading}
                                    padding={
                                        isDesktop && enableBloomreachSuggestions
                                            ? '0 0 0 30px'
                                            : '0px'
                                    }
                                >
                                    <FormattedMessage defaultMessage="Keywords" id="keywords" />
                                </Heading>
                                <Suggestions
                                    closeAndNavigate={closeAndNavigate}
                                    phraseResponses={phraseResponses}
                                    setProductSuggestionsToRender={(products) => {
                                        setProductSuggestionsToRender(products)
                                    }}
                                    setRecommendedForText={setRecommendedForText}
                                    enableBloomreachSuggestions={enableBloomreachSuggestions}
                                    suggestions={searchSuggestions?.phraseSuggestion}
                                    isCategory={false}
                                    totalSuggestionsLength={
                                        searchSuggestions?.productSuggestions?.length
                                    }
                                    searchQuery={searchQuery}
                                />
                            </Text>
                        </Box>
                    ) : (
                        <></>
                    )}
                    {!enableBloomreachSuggestions &&
                        searchSuggestions?.phraseSuggestion &&
                        searchQuery && (
                            <Box className="search-phase-wrapper">
                                <Text
                                    fontSize={{base: '16px', lg: '14px'}}
                                    className="suggested-term"
                                    display="inline-flex"
                                    margin={{lg: '0 30px'}}
                                    color={{base: 'gray.800', lg: 'black'}}
                                >
                                    <FormattedMessage defaultMessage="Search For" id="search.for" />
                                    &nbsp;
                                    <Link
                                        {...suggestionsStyle.phaseCategoriesWrapper.name}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            tagManager.siteSearch(
                                                searchQuery,
                                                searchSuggestions?.productSuggestions?.length?.toString() ||
                                                    '0',
                                                undefined,
                                                'selected-suggested-term',
                                                searchSuggestions?.phraseSuggestion
                                            )
                                            closeAndNavigate(
                                                searchUrlBuilder(
                                                    isYR,
                                                    searchSuggestions?.phraseSuggestion
                                                ),
                                                true,
                                                false
                                            )
                                        }}
                                    >
                                        {searchSuggestions?.phraseSuggestion}
                                    </Link>
                                </Text>
                            </Box>
                        )}
                    {searchSuggestions?.categorySuggestions?.length && searchQuery && (
                        <Box
                            className="category-search-suggestions"
                            display={{base: 'none', md: 'none', lg: 'block'}}
                        >
                            <Heading
                                {...suggestionsStyle.phaseCategoriesWrapper.heading}
                                padding={{lg: '0 0 0 30px'}}
                            >
                                <FormattedMessage
                                    defaultMessage="Categories"
                                    id="search.categories"
                                />
                            </Heading>
                            <Suggestions
                                closeAndNavigate={closeAndNavigate}
                                enableBloomreachSuggestions={enableBloomreachSuggestions}
                                suggestions={searchSuggestions?.categorySuggestions}
                                isCategory={true}
                                setRecommendedForText={setRecommendedForText}
                                setProductSuggestionsToRender={setProductSuggestionsToRender}
                                categoryResponses={categoryResponses}
                                totalSuggestionsLength={
                                    searchSuggestions?.productSuggestions?.length
                                }
                                searchQuery={searchQuery}
                            />
                        </Box>
                    )}
                    <Box>
                        {recentSearches?.length ? (
                            <RecentSearchesWrapper
                                recentSearches={recentSearches}
                                closeAndNavigate={closeAndNavigate}
                                enableBloomreachSuggestions={enableBloomreachSuggestions}
                                totalSuggestionsLength={
                                    searchSuggestions?.productSuggestions?.length
                                }
                                searchQuery={searchQuery}
                            />
                        ) : (
                            <></>
                        )}
                    </Box>
                </Box>
                {(!searchSuggestions?.productSuggestions?.length || !searchQuery) && (
                    <Box
                        marginTop={{base: !recentSearches?.length ? '20px' : '0px', lg: '0px'}}
                        width={recentSearches?.length ? '60%' : '100vw'}
                    >
                        <EinsteinSearchSuggestions
                            closeAndNavigate={closeAndNavigate}
                            searchQuery={searchQuery}
                            recentSearches={recentSearches}
                        />
                    </Box>
                )}
                {searchSuggestions?.categorySuggestions?.length && searchQuery && (
                    <Box
                        className="category-search-suggestions"
                        {...suggestionsStyle.mobileCategories.main}
                    >
                        <Heading {...suggestionsStyle.mobileCategories.heading}>
                            <FormattedMessage defaultMessage="Categories" id="search.categories" />
                        </Heading>
                        <Suggestions
                            closeAndNavigate={closeAndNavigate}
                            enableBloomreachSuggestions={enableBloomreachSuggestions}
                            suggestions={searchSuggestions?.categorySuggestions}
                            isCategory={true}
                            setRecommendedForText={setRecommendedForText}
                            setProductSuggestionsToRender={setProductSuggestionsToRender}
                            categoryResponses={categoryResponses}
                            totalSuggestionsLength={searchSuggestions?.productSuggestions?.length}
                            searchQuery={searchQuery}
                        />
                    </Box>
                )}
                {searchQuery && searchSuggestions?.productSuggestions?.length && (
                    <Box
                        className="product-suggestions-wrapper"
                        {...suggestionsStyle.productSuggestions.main}
                    >
                        <Heading
                            {...suggestionsStyle.productSuggestions.heading}
                            display="flex"
                            padding="0px !important"
                        >
                            {enableBloomreachSuggestions ? (
                                <>
                                    <FormattedMessage
                                        defaultMessage="Recommended for"
                                        id="recommended.for"
                                    />
                                    <Box color="purple.600" fontWeight="400">
                                        &nbsp;
                                        {recommendedForText?.replace(/<[^>]+>/g, '') || searchQuery}
                                    </Box>
                                </>
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Product Suggestions"
                                    id="search.product.suggestions"
                                />
                            )}
                        </Heading>
                        {isDesktop ? (
                            // Desktop layout: 2 tiles per row
                            <Box
                                position="relative"
                                maxWidth="-webkit-fill-available"
                                width="100%"
                                sx={{
                                    '.image-wrapper, .chakra-heading': {
                                        marginBottom: '10px'
                                    },
                                    '.swiper': {
                                        padding: '0 20px 0 0 !important',
                                        margin: '0 !important',
                                        overflowX: 'auto',
                                        WebkitOverflowScrolling: 'touch',
                                        position: 'static',
                                        /* Hide scrollbar */
                                        '&::-webkit-scrollbar': {
                                            display: 'none'
                                        },
                                        scrollbarWidth: 'none', // for Firefox
                                        msOverflowStyle: 'none' // for IE and Edge
                                    },
                                    '.swiper-wrapper': {
                                        paddingLeft: {base: '20px', xs: '30px', lg: '40px'}
                                    },
                                    '.swiper-button-next': {
                                        right: '0px',
                                        top: '-63px'
                                    },

                                    '.swiper-button-prev': {
                                        right: '50px',
                                        top: '-63px',
                                        left: 'initial'
                                    },
                                    '.swiper-slide': {
                                        width: '152px !important'
                                    }
                                }}
                            >
                                <Box
                                    width="100%"
                                    className="swiper-parent"
                                    sx={{
                                        '.swiper': {
                                            paddingRight: '20px',
                                            paddingTop: '47px',
                                            marginTop: '-7px'
                                        },
                                        '.swiper-wrapper': {
                                            paddingLeft: '0px'
                                        },
                                        '.swiper-slide': {
                                            width: {base: '175px', lg: '300px'}
                                        }
                                    }}
                                >
                                    {/* Swiper Carousel */}
                                    <Swiper
                                        navigation={true}
                                        slidesPerView="auto"
                                        spaceBetween="20px"
                                        modules={[Navigation]}
                                    >
                                        {/* Slides */}
                                        {productSuggestionsToRender
                                            ?.slice(0, 10)
                                            ?.map((product, idx) => (
                                                <SwiperSlide key={idx}>
                                                    <Box>
                                                        <SearchSugggestionTile
                                                            index={idx}
                                                            closeAndNavigate={closeAndNavigate}
                                                            product={product}
                                                            searchQuery={searchQuery}
                                                            totalSuggestionsLength={
                                                                searchSuggestions
                                                                    ?.productSuggestions?.length
                                                            }
                                                        />
                                                    </Box>
                                                </SwiperSlide>
                                            ))}
                                    </Swiper>
                                </Box>
                            </Box>
                        ) : (
                            // Mobile layout: horizontal scroll
                            <Box
                                {...suggestionsStyle.productSuggestions.mobileLayout}
                                width="calc(100vw - 30px)"
                                paddingInlineEnd="25px"
                                gap="20px"
                                className="product-suggestions"
                            >
                                {productSuggestionsToRender?.map((product, idx) => (
                                    <Box
                                        key={idx}
                                        {...suggestionsStyle.productSuggestions.suggestionBox}
                                    >
                                        <SearchSugggestionTile
                                            index={idx}
                                            closeAndNavigate={closeAndNavigate}
                                            product={product}
                                            searchQuery={searchQuery}
                                            totalSuggestionsLength={
                                                searchSuggestions?.productSuggestions?.length
                                            }
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </Fragment>
        </Stack>
    )
}

export const RecentSearchesWrapper = ({
    recentSearches,
    closeAndNavigate,
    totalSuggestionsLength,
    enableBloomreachSuggestions,
    searchQuery
}) => {
    const suggestionsStyle = useStyleConfig('SuggestionsStyle')
    return (
        <Box className="recent-searches" marginBottom="20px" width="fit-content">
            <Heading
                {...suggestionsStyle.phaseCategoriesWrapper.heading}
                padding={{base: '0px', lg: '0 0 0 30px'}}
            >
                <FormattedMessage
                    defaultMessage="Your Recent Searches"
                    id="search.recent.searches"
                />
            </Heading>
            <RecentSearches
                recentSearches={recentSearches}
                closeAndNavigate={closeAndNavigate}
                enableBloomreachSuggestions={enableBloomreachSuggestions}
                totalSuggestionsLength={totalSuggestionsLength}
                searchQuery={searchQuery}
            />
        </Box>
    )
}

RecentSearchesWrapper.propTypes = {
    recentSearches: PropTypes.array,
    enableBloomreachSuggestions: PropTypes.bool,
    searchQuery: PropTypes.string,
    totalSuggestionsLength: PropTypes.any,
    closeAndNavigate: PropTypes.func
}

SearchSuggestions.propTypes = {
    recentSearches: PropTypes.array,
    searchSuggestions: PropTypes.object,
    phraseResponses: PropTypes.object,
    categoryResponses: PropTypes.object,
    enableBloomreachSuggestions: PropTypes.bool,
    closeAndNavigate: PropTypes.func,
    searchQuery: PropTypes.string
}

export default SearchSuggestions
